import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { AlertTriangle, Eye, RotateCcw, Search, Lock, Unlock } from 'lucide-react';
import { format } from 'date-fns';

export default function AdminUserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [strikeModalOpen, setStrikeModalOpen] = useState(false);
  const [strikeData, setStrikeData] = useState(null);
  const [loadingStrikes, setLoadingStrikes] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStrikes = async (user) => {
    setSelectedUser(user);
    setStrikeModalOpen(true);
    setLoadingStrikes(true);

    try {
      const response = await axiosInstance.get(`/users/${user.id}/strikes`);
      setStrikeData(response.data);
    } catch (error) {
      console.error('Failed to fetch strikes:', error);
      toast.error('Failed to load strike data');
      setStrikeData(null);
    } finally {
      setLoadingStrikes(false);
    }
  };

  const handleResetStrikes = async (user) => {
    if (!window.confirm(`Are you sure you want to reset strikes and unlock ${user.name}'s account?`)) {
      return;
    }

    try {
      await axiosInstance.put(`/users/${user.id}/reset-strikes`);
      toast.success(`Strikes reset for ${user.name}. Account unlocked.`);

      // Refresh strike data if modal is open
      if (strikeModalOpen && selectedUser?.id === user.id) {
        const response = await axiosInstance.get(`/users/${user.id}/strikes`);
        setStrikeData(response.data);
      }

      // Refresh user list
      fetchUsers();
    } catch (error) {
      console.error('Failed to reset strikes:', error);
      toast.error('Failed to reset strikes');
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query)
    );
  });

  // Get strike count display class
  const getStrikeClass = (strikeCount) => {
    if (strikeCount >= 3) return 'text-red-600 font-bold';
    if (strikeCount > 0) return 'text-amber-600 font-medium';
    return 'text-gray-600';
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>User Management</span>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <Button variant="outline" onClick={fetchUsers}>
                Refresh
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading users...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-center">Strikes</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 rounded capitalize">
                        {user.role?.replace('_', ' ')}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className={getStrikeClass(user.strike_count || 0)}>
                          {user.strike_count || 0}/3
                        </span>
                        {(user.strike_count || 0) > 0 && (
                          <AlertTriangle
                            className={`h-4 w-4 ${
                              (user.strike_count || 0) >= 3 ? 'text-red-500' : 'text-amber-500'
                            }`}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {user.account_locked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                          <Lock className="h-3 w-3" />
                          Locked
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                          <Unlock className="h-3 w-3" />
                          Active
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewStrikes(user)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Strikes
                        </Button>
                        {((user.strike_count || 0) > 0 || user.account_locked) && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleResetStrikes(user)}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Reset Strikes
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      {searchQuery ? 'No users match your search' : 'No users found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Strike History Modal */}
      <Dialog open={strikeModalOpen} onOpenChange={setStrikeModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Strike History: {selectedUser?.name}</DialogTitle>
          </DialogHeader>

          {loadingStrikes ? (
            <div className="text-center py-8 text-gray-500">Loading strike data...</div>
          ) : strikeData ? (
            <div className="space-y-4">
              {/* Strike Summary */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <span className={`text-2xl font-bold ${getStrikeClass(strikeData.strike_count)}`}>
                    {strikeData.strike_count}
                  </span>
                  <span className="text-gray-500">/{strikeData.max_strikes} strikes</span>
                </div>
                {strikeData.is_locked && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full font-medium flex items-center gap-1">
                    <Lock className="h-4 w-4" />
                    Account Locked
                  </span>
                )}
              </div>

              {/* Strike Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    strikeData.strike_count >= 3 ? 'bg-red-500' :
                    strikeData.strike_count > 0 ? 'bg-amber-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min((strikeData.strike_count / strikeData.max_strikes) * 100, 100)}%` }}
                />
              </div>

              {/* Strike History List */}
              <div>
                <h4 className="font-medium mb-2">History</h4>
                {strikeData.strike_history?.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {strikeData.strike_history.map((strike, i) => (
                      <div key={i} className="p-3 border rounded bg-white">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{strike.date}</span>
                          <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                            Strike #{strikeData.strike_history.length - i}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Rejected by {strike.rejected_by || 'Admin'}
                          {strike.rejected_at && (
                            <> on {format(new Date(strike.rejected_at), 'PPp')}</>
                          )}
                        </div>
                        {strike.notes && (
                          <div className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                            {strike.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No strike history</p>
                )}
              </div>

              {/* Reset Button in Modal */}
              {(strikeData.strike_count > 0 || strikeData.is_locked) && (
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => {
                    handleResetStrikes(selectedUser);
                  }}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset All Strikes & Unlock Account
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Failed to load strike data</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
